import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRouter, usePathname } from 'next/navigation';
import useWorldStore from '../store/worldStore';
import UserProfileDropdown from './UserProfileDropdown';
import { SettingsIcon, UserIcon, UsersIcon, ActivityIcon, ZapIcon, MessageIcon, PhoneIcon, GlobeIcon, CubeIcon, TrashIcon, PlusIcon } from '@/components/icons';
import ConfirmModal from './character/ConfirmModal';
import useWorldDeleteAgent from '../hooks/useWorldDeleteAgent';

const mono = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };

/** Portal wrapper — renders dropdown fixed-positioned relative to an anchor button, in document.body */
function PortalDropdown({ anchorRef, children, anchor = 'bottom-left' }) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    if (anchor === 'bottom-right') {
      setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    } else if (anchor === 'bottom-left') {
      setPos({ top: rect.bottom + 6, left: rect.left });
    } else if (anchor === 'top-right') {
      setPos({ bottom: window.innerHeight - rect.top + 8, right: window.innerWidth - rect.right });
    }
  }, [anchorRef, anchor]);

  if (!pos) return null;
  return createPortal(
    <div style={{ position: 'fixed', zIndex: 200, ...pos }}>
      {children}
    </div>,
    document.body
  );
}

const CONTROLS = [
  { key: 'Right drag', action: 'Rotate' },
  { key: 'Middle drag', action: 'Pan' },
  { key: 'Scroll', action: 'Zoom' },
  { key: 'Left click', action: 'Select agent' },
  { key: 'Left drag', action: 'Move agent' },
  { key: 'Hover', action: 'Wave / Info' },
  { key: 'Num 1', action: 'Call Nova' },
  { key: 'Num 2', action: 'Call Cipher' },
  { key: 'Num 3', action: 'Call Echo' },
  { key: 'M', action: 'Toggle view' },
];

function useClickOutside(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

/* ── Agent Dropdown ── */
function AgentRow({ agent, onSelect, onMessage, onCall, onRequestDelete }) {
  const [hovered, setHovered] = useState(false);

  const handleDelete = (e) => {
    e.stopPropagation();
    onRequestDelete(agent);
  };

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 8px',
        borderRadius: 8, cursor: 'pointer',
        transition: 'background 0.1s',
        background: hovered ? 'rgba(255,255,255,0.05)' : 'transparent',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          width: 14, height: 14, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
        }}
      >
        {hovered ? (
          <button
            onClick={handleDelete}
            title="Delete agent"
            aria-label={`Delete ${agent.name}`}
            style={{
              background: 'none', border: 'none',
              padding: 0, margin: 0,
              cursor: 'pointer',
              color: 'rgba(255,120,120,0.85)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <TrashIcon size={12} />
          </button>
        ) : (
          <div style={{
            width: 8, height: 8, borderRadius: 9999,
            background: agent.color,
          }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}
        onClick={() => onSelect(agent.id)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...mono, fontSize: 11, fontWeight: 500, color: '#fcfcfc' }}>
            {agent.name}
          </span>
          <span style={{ ...mono, fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>
            {agent.role}
          </span>
        </div>
        <div style={{
          ...mono, fontSize: 9,
          color: '#a855f7',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          🏠 {agent.currentTask || 'Standby'}
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onMessage(agent.id); }}
        title="Message"
        style={{
          background: 'none', border: 'none', fontSize: 12,
          cursor: 'pointer', padding: '2px 4px',
          color: 'rgba(255,255,255,0.4)', flexShrink: 0,
        }}
      >
        <MessageIcon size={12} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onCall(agent.id); }}
        title="Call"
        style={{
          background: 'none', border: 'none', fontSize: 12,
          cursor: 'pointer', padding: '2px 4px',
          color: 'rgba(255,255,255,0.4)', flexShrink: 0,
        }}
      >
        <PhoneIcon size={12} />
      </button>
    </div>
  );
}

function AgentDropdown({ onClose, onOpenCreator }) {
  const agents = useWorldStore((s) => s.agents);
  const selectAgent = useWorldStore((s) => s.selectAgent);
  const openChat = useWorldStore((s) => s.openChat);
  const startCall = useWorldStore((s) => s.startCall);
  const { deleteAgentFromWorld, getDeleteState, deleteErrorMessage } = useWorldDeleteAgent();
  const [confirmingAgent, setConfirmingAgent] = useState(null);
  const ref = useRef();
  useClickOutside(ref, () => {
    if (!confirmingAgent) onClose();
  });

  const handleSelect = (id) => {
    selectAgent(id);
    onClose();
  };
  const handleMessage = (id) => {
    openChat(id);
    onClose();
  };
  const handleCall = (id) => {
    startCall(id);
    onClose();
  };
  const deleteState = confirmingAgent ? getDeleteState(confirmingAgent.id) : 'idle';

  const confirmDelete = async () => {
    if (!confirmingAgent) return;
    if (deleteState === 'loading') return;
    const agentToDelete = confirmingAgent;
    const deleted = await deleteAgentFromWorld(agentToDelete);
    if (deleted) setConfirmingAgent(null);
  };

  return (
    <>
      <div ref={ref} style={{
        width: 280, background: 'rgba(6,6,7,0.85)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: 'none',
        borderRadius: 16, overflow: 'hidden', zIndex: 200,
        padding: 6,
        boxShadow: '0px 8px 32px rgba(0,0,0,0.3)',
        animation: 'dropUp 0.15s ease-out',
        display: 'flex', flexDirection: 'column', gap: 4,
      }}>
        <style>{`@keyframes dropUp { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }`}</style>

        {/* Agent list block */}
        <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 12, padding: 6, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {agents.map((agent) => (
            <AgentRow
              key={agent.id}
              agent={agent}
              onSelect={handleSelect}
              onMessage={handleMessage}
              onCall={handleCall}
              onRequestDelete={setConfirmingAgent}
            />
          ))}
          {agents.length === 0 && (
            <div style={{
              ...mono, fontSize: 10, color: 'rgba(255,255,255,0.35)',
              padding: '10px 8px', textAlign: 'center',
            }}>
              No agents yet
            </div>
          )}
        </div>

        {/* Add agent block */}
        <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 12, padding: 6 }}>
          <button
            onClick={() => { onOpenCreator?.(); onClose(); }}
            style={{
              width: '100%',
              padding: '7px 8px', cursor: 'pointer',
              ...mono, fontSize: 11, fontWeight: 500,
              color: 'rgba(255,255,255,0.75)',
              background: 'none', border: 'none',
              textAlign: 'center', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background 0.1s, color 0.1s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}
          >
            <PlusIcon size={12} />
            <span>Add agent</span>
          </button>
        </div>
      </div>

      <ConfirmModal
        open={Boolean(confirmingAgent)}
        title="Permanently delete this agent?"
        description={
          confirmingAgent
            ? `${confirmingAgent.name} and all of their tasks, chats, and runtime state will be removed from your world. This cannot be undone.`
            : undefined
        }
        errorMessage={deleteState === 'error' ? deleteErrorMessage : null}
        confirmLabel={deleteState === 'loading' ? 'Deleting…' : 'Delete agent'}
        tone="destructive"
        onCancel={() => setConfirmingAgent(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}

/* ── Settings Dropdown ── */
function SettingsDropdown({ onClose }) {
  const ref = useRef();
  useClickOutside(ref, onClose);

  return (
    <div ref={ref} style={{
      width: 220, background: 'rgba(6,6,7,0.85)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 12, padding: '10px 12px',
      boxShadow: '0px 8px 32px rgba(0,0,0,0.3)',
      animation: 'dropIn 0.15s ease-out',
    }}>
      <div style={{
        ...mono, fontSize: 9,
        color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase',
        letterSpacing: '0.08em', marginBottom: 8,
      }}>
        Controls
      </div>
      {CONTROLS.map((r) => (
        <div key={r.key} style={{
          display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 2,
        }}>
          <span style={{
            ...mono, fontSize: 9,
            color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            {r.key}
          </span>
          <span style={{ ...mono, fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
            {r.action}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── TopBar ── */
export default function TopBar({ showDashboardControls: showDashboardControlsOverride }) {
  const agents = useWorldStore((s) => s.agents);
  const activeView = useWorldStore((s) => s.activeView);
  const setActiveView = useWorldStore((s) => s.setActiveView);
  const toggleActivityFeed = useWorldStore((s) => s.toggleActivityFeed);
  const activityFeedVisible = useWorldStore((s) => s.activityFeedVisible);
  const toggleTaskManager = useWorldStore((s) => s.toggleTaskManager);
  const taskManagerOpen = useWorldStore((s) => s.taskManagerOpen);
  const activeTasks = useWorldStore((s) => s.activeTasks);
  const taskQueues = useWorldStore((s) => s.taskQueues);

  const totalTasks = Object.values(activeTasks).filter((t) => t?.status === 'running').length
    + Object.values(taskQueues).reduce((sum, q) => sum + (q?.length || 0), 0);

  const settingsBtnRef = useRef(null);
  const profileBtnRef = useRef(null);
  const agentBtnRef = useRef(null);

  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showAgents, setShowAgents] = useState(false);
  const setShowCreator = useWorldStore((s) => s.setShowCreator);

  // Track when dropdowns were closed by click-outside to prevent immediate reopen
  const closedAtRef = useRef({ settings: 0, profile: 0, agents: 0 });
  const safeToggle = (key, current, setter) => {
    if (current) {
      setter(false);
    } else if (Date.now() - closedAtRef.current[key] > 200) {
      setter(true);
    }
  };
  const closeWithTimestamp = (key, setter) => () => {
    closedAtRef.current[key] = Date.now();
    setter(false);
  };

  const router = useRouter();
  const currentPath = usePathname();

  const navLinks = [
    { id: 'dashboard', label: 'Dashboard', href: '/' },
    { id: 'characters', label: 'Characters', href: '/characters' },
    { id: 'skills', label: 'Skills', href: '/skills' },
  ];

  const activeNav = currentPath?.startsWith('/skills') ? 'skills'
    : currentPath?.startsWith('/characters') ? 'characters'
    : 'dashboard';
  const showDashboardControls =
    typeof showDashboardControlsOverride === 'boolean'
      ? showDashboardControlsOverride && activeNav === 'dashboard'
      : activeNav === 'dashboard';

  return (
    <>
      {/* ── Main TopBar ── */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        height: 40, zIndex: 95,
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 12px',
      }}>
        {/* Left: Settings (no bg) */}
        <div>
          <button ref={settingsBtnRef}
            onClick={() => { safeToggle('settings', showSettings, setShowSettings); setShowProfile(false); }}
            title="Settings"
            style={{
              background: 'none', border: 'none', padding: '4px',
              color: 'rgba(255,255,255,0.4)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
          >
            <SettingsIcon size={15} />
          </button>
          {showSettings && <PortalDropdown anchorRef={settingsBtnRef} anchor="bottom-left"><SettingsDropdown onClose={closeWithTimestamp('settings', setShowSettings)} /></PortalDropdown>}
        </div>

        {/* Center: Nav tabs (text only, no icons) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {navLinks.map((link) => (
            <button key={link.id}
              onClick={() => {
                if (link.href !== currentPath) router.push(link.href);
              }}
              style={{
                ...mono, fontSize: 11, fontWeight: 500,
                padding: '4px 14px',
                background: 'none', border: 'none',
                color: activeNav === link.id ? '#fcfcfc' : 'rgba(255,255,255,0.35)',
                cursor: 'pointer',
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => { if (activeNav !== link.id) e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
              onMouseLeave={(e) => { if (activeNav !== link.id) e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* Right: Profile (no bg) */}
        <div>
          <button ref={profileBtnRef}
            onClick={() => { safeToggle('profile', showProfile, setShowProfile); setShowSettings(false); }}
            title="Profile"
            style={{
              background: 'none', border: 'none', padding: '4px',
              color: 'rgba(255,255,255,0.4)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
          >
            <UserIcon size={15} />
          </button>
          {showProfile && <PortalDropdown anchorRef={profileBtnRef} anchor="bottom-right"><UserProfileDropdown onClose={closeWithTimestamp('profile', setShowProfile)} /></PortalDropdown>}
        </div>
      </div>

      {showDashboardControls && <div style={{
        position: 'fixed', top: 48, left: '50%', transform: 'translateX(-50%)',
        zIndex: 94,
        display: 'flex', alignItems: 'center', gap: 1,
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        borderRadius: 16,
        padding: '3px 4px',
        boxShadow: 'inset 0px 1px 1px rgba(255,255,255,0.06)',
      }}>
        {[
          { id: 'world', label: 'World', icon: <GlobeIcon size={12} /> },
          { id: 'agent', label: 'Agents', icon: <CubeIcon size={12} /> },
        ].map((v) => (
          <button key={v.id} onClick={() => setActiveView(v.id)} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            ...mono, fontSize: 10, fontWeight: 500,
            padding: '3px 10px',
            background: activeView === v.id ? 'rgba(216,224,255,0.16)' : 'transparent',
            borderRadius: 12,
            color: activeView === v.id ? '#fcfcfc' : 'rgba(255,255,255,0.3)',
            border: 'none', cursor: 'pointer',
            transition: 'all 0.12s',
          }}
            onMouseEnter={(e) => { if (activeView !== v.id) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            onMouseLeave={(e) => { if (activeView !== v.id) e.currentTarget.style.background = 'transparent'; }}
          >
            {v.icon} {v.label}
          </button>
        ))}
      </div>}

      <button onClick={toggleActivityFeed} title="Activities" style={{
        position: 'fixed', top: 48, left: 12, zIndex: 94,
        height: 28, borderRadius: 14,
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        border: 'none',
        padding: '0 12px',
        ...mono, fontSize: 10, fontWeight: 500,
        color: activityFeedVisible ? 'rgba(214,187,251,0.9)' : 'rgba(255,255,255,0.4)',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 5,
        boxShadow: 'inset 0px 1px 1px rgba(255,255,255,0.06)',
        transition: 'all 0.15s',
      }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = activityFeedVisible ? 'rgba(214,187,251,0.9)' : 'rgba(255,255,255,0.4)'; }}
      >
        <ActivityIcon size={12} /> Activities
      </button>

      <button onClick={() => { toggleTaskManager(); }} title="Tasks" style={{
        position: 'fixed', top: 48, right: 12, zIndex: 94,
        height: 28, borderRadius: 14,
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        border: 'none',
        padding: '0 12px',
        ...mono, fontSize: 10, fontWeight: 500,
        color: taskManagerOpen ? '#fbbf24' : 'rgba(255,255,255,0.4)',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 5,
        boxShadow: 'inset 0px 1px 1px rgba(255,255,255,0.06)',
        transition: 'all 0.15s',
      }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = taskManagerOpen ? '#fbbf24' : 'rgba(255,255,255,0.4)'; }}
      >
        <ZapIcon size={12} /> Tasks {totalTasks > 0 && <span style={{
          background: '#fbbf24', color: '#060607', borderRadius: 6,
          padding: '0 4px', fontSize: 8, fontWeight: 600, marginLeft: 2,
        }}>{totalTasks}</span>}
      </button>

      <div style={{ position: 'fixed', bottom: 12, right: 12, zIndex: 92 }}>
        <div style={{ position: 'relative' }}>
          <button ref={agentBtnRef}
            onClick={() => { safeToggle('agents', showAgents, setShowAgents); }}
            title="Agents"
            style={{
              width: 40, height: 40, borderRadius: 20,
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(20px)',
              border: 'none',
              color: showAgents ? '#fcfcfc' : 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: 'inset 0px 1px 1px rgba(255,255,255,0.06)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = showAgents ? '#fcfcfc' : 'rgba(255,255,255,0.4)'; }}
          >
            <UsersIcon size={16} />
          </button>
          {agents.length > 0 && (
            <div style={{
              position: 'absolute', top: -3, right: -3,
              minWidth: 16, height: 16, borderRadius: '50%',
              background: '#4ADE80',
              color: '#060607',
              ...mono, fontSize: 10, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {agents.length}
            </div>
          )}
          {showAgents && <PortalDropdown anchorRef={agentBtnRef} anchor="top-right"><AgentDropdown onClose={closeWithTimestamp('agents', setShowAgents)} onOpenCreator={() => setShowCreator(true)} /></PortalDropdown>}
        </div>
      </div>

      <style>{`@keyframes dropIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </>
  );
}
